import { useAuth } from 'modules/auth/hooks/useAuth';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useJWTManagerPermissions } from 'domains/jwtToken/hooks/useJWTManagerPermissions';

export interface ProjectsPageLoaderOptions {
  /**
   * Whether to include isLoggedIn in the shouldForceRedirect calculation
   * @default false
   */
  includeIsLoggedInInRedirect?: boolean;
}

export interface ProjectsPageLoaderResult {
  isLoading: boolean;
  shouldForceRedirect: boolean;
  isFreemium: boolean;
  hasProjectsAccess: boolean;
}

/**
 * Reusable hook for projects page loading and access control logic
 * Used by ProjectPageLoader and EditProjectStepPageV2Loader
 */
export const useProjectsPageLoader = (
  options: ProjectsPageLoaderOptions = {},
): ProjectsPageLoaderResult => {
  const { includeIsLoggedInInRedirect = false } = options;

  const { authToken, isLoggedIn, isLoggingIn } = useAuth();
  const { isAccountInitializing, permissionsInitialized } = useAccount();

  const {
    allowedJWTsCountLoading,
    hasReadAccess: hasProjectsAccess,
    isAllowedJWTsCountLoaded,
  } = useJWTManagerPermissions();

  const { initialLoading: accountStatusLoading, isFreemium } =
    useAccountStatus();

  const hasAuthToken = Boolean(authToken);
  const shouldLogIn = hasAuthToken && !isLoggedIn;

  const isLoading =
    isLoggingIn ||
    shouldLogIn ||
    isAccountInitializing ||
    accountStatusLoading ||
    allowedJWTsCountLoading ||
    !permissionsInitialized;

  const shouldForceRedirect = includeIsLoggedInInRedirect
    ? isLoggedIn &&
      !isFreemium &&
      !hasProjectsAccess &&
      isAllowedJWTsCountLoaded
    : !isFreemium && !hasProjectsAccess && isAllowedJWTsCountLoaded;

  return {
    isLoading,
    shouldForceRedirect,
    isFreemium,
    hasProjectsAccess,
  };
};
